<script setup lang="ts">
import { computed, onMounted, PropType, ref } from "vue";
import {
    Department,
    GroupChartSummary,
    Participant,
    ProductLanguages,
    Project,
    ProjectRole,
} from "../../../types/PersologOnlineTypes.ts";
import { ProfileContractorAPI } from "../../../api";
import { useToast } from "primevue/usetoast";
import { getDisplayName } from "../../../utils/GeneralUtils.ts";
import { pickGenerationLanguage } from "../../../utils/LanguageUtils.ts";
import { useI18n } from "vue-i18n";
import { getDepartments, GROUP_CHART_FOR_361, groupChartToastGroup, HEXACO_GROUP_CHART } from "../../../utils/ProjectUtils.ts";

const props = defineProps({
    project: {
        type: Object as PropType<Project>,
        required: true,
    },
    participants: {
        type: Array as PropType<Participant[]>,
        default: () => [],
    },
    groupChartProduct: {
        type: String,
        required: true,
    },
    languages: {
        type: Object as PropType<ProductLanguages | null>,
        default: null,
    },
    /** The project's roles with their display names, to label participants and check the 361 selection. */
    roles: {
        type: Array as PropType<ProjectRole[]>,
        default: () => [],
    },
});

const emit = defineEmits<{
    (e: "created", chart: GroupChartSummary): void;
    (e: "close"): void;
}>();

const { t, locale } = useI18n();
const toast = useToast();
const toastGroup = groupChartToastGroup(props.project.id);

const visible = ref(true);
const loading = ref(false);

// The 361 group chart compares the feedback with the feedback recipient's self-assessment, so it needs
// exactly one feedback recipient and at least one person who gave feedback (RDT-35).
const is361 = computed(() => props.groupChartProduct === GROUP_CHART_FOR_361);
const feedbackRecipientRole = computed(() => props.roles.find((role) => role.role === "FEEDBACK_RECIPIENT"));
const isFeedbackRecipient = (participant: Participant) =>
    !!feedbackRecipientRole.value && participant.roleId === feedbackRecipientRole.value.id;

// Start with the feedback recipient selected: the chart is about them, and the trainer only adds the others.
const recipients = props.participants.filter(isFeedbackRecipient);
const selectedParticipants = ref<Participant[]>(is361.value && recipients.length === 1 ? recipients : []);

/** The i18n key explaining why the selection cannot make a 361 group chart, or null when it can. */
const selectionProblem = computed(() => {
    if (!is361.value) return null;
    const recipientCount = selectedParticipants.value.filter(isFeedbackRecipient).length;
    if (recipientCount === 0) return "feedback-recipient-missing";
    if (recipientCount > 1) return "feedback-recipient-multiple";
    if (selectedParticipants.value.length === recipientCount) return "feedback-givers-missing";
    return null;
});

const label = ref("");
const withName = ref(true);
const departments = ref<Department[]>([]);
const productLanguages = computed(() => props.languages?.[props.groupChartProduct] ?? []);
// The "include participant data" toggle only affects the HEXACO group report (in1002).
const showWithNameToggle = computed(() => props.groupChartProduct === HEXACO_GROUP_CHART);

const customLabel = (option: Participant) => {
    const department = departments.value.find((d) => d.id === option.departmentId)?.name;
    const role = props.roles.find((r) => r.id === option.roleId)?.name;
    const details = [department, role].filter(Boolean).join(", ");
    return details ? `${option.firstName} ${option.name} (${details})` : `${option.firstName} ${option.name}`;
};

async function generate() {
    if (selectedParticipants.value.length === 0 || selectionProblem.value || loading.value) return;
    const language = findSuitableLanguage();
    loading.value = true;
    try {
        const chart = await ProfileContractorAPI.generateGroupChart(
            props.groupChartProduct,
            props.project.id as number,
            { participants: selectedParticipants.value },
            language,
            label.value.trim() || undefined,
            withName.value,
        );
        if (verifyProductLanguage(props.project.language)) {
            showSuccessToast(language);
        } else {
            showWarningToast(props.project.language, language);
        }
        emit("created", chart);
        closeDialog();
    } catch (error) {
        console.error("Error generating group chart:", error);
        toast.add({
            group: toastGroup,
            severity: "error",
            summary: t("project-details.group-chart.toasts.heading-generation"),
            detail: t("project-details.group-chart.toasts.generation-error"),
            life: 8000,
        });
    } finally {
        loading.value = false;
    }
}

function closeDialog() {
    visible.value = false;
    emit("close");
}

function verifyProductLanguage(language: string) {
    return productLanguages.value.includes(language);
}

function findSuitableLanguage() {
    return pickGenerationLanguage(productLanguages.value, props.project.language);
}

const showSuccessToast = (language: string) => {
    toast.add({
        group: toastGroup,
        severity: "success",
        summary: t("project-details.group-chart.toasts.heading-generation"),
        detail: t("project-details.group-chart.toasts.success", {
            language: getDisplayName(locale.value, language),
        }),
        life: 8000,
    });
};

const showWarningToast = (missingLanguage: string, language: string) => {
    toast.add({
        group: toastGroup,
        severity: "warn",
        summary: t("project-details.group-chart.toasts.heading-generation"),
        detail: t("project-details.group-chart.toasts.warning", {
            missingLanguage: getDisplayName(locale.value, missingLanguage),
            language: getDisplayName(locale.value, language),
        }),
        life: 8000,
    });
};

onMounted(async () => {
    departments.value = await getDepartments(props.project);
});
</script>

<template>
    <Dialog
        v-model:visible="visible"
        modal
        :header="t('project-details.group-chart.create.title')"
        :style="{ width: '32rem' }"
        :closable="false"
        :dismissable-mask="false"
        :block-scroll="true"
        :draggable="false"
        @on-hide="closeDialog"
    >
        <div class="flex flex-col gap-4 mb-2">
            <div class="flex flex-col gap-1">
                <label :for="`group-chart-create_label-${project.id}`" class="text-sm font-bold">
                    {{ t("project-details.group-chart.create.label-field") }}
                </label>
                <InputText
                    :id="`group-chart-create_label-${project.id}`"
                    v-model="label"
                    :placeholder="t('project-details.group-chart.create.label-placeholder')"
                    class="w-full"
                />
            </div>
            <div class="flex flex-col gap-1">
                <label :for="`group-chart-create_participants-${project.id}`" class="text-sm font-bold">
                    {{ t("project-details.group-chart.create.participants-field") }}
                </label>
                <MultiSelect
                    :id="`group-chart-create_participants-${project.id}`"
                    v-model="selectedParticipants"
                    :options="participants"
                    :option-label="customLabel"
                    filter
                    :placeholder="t('project-details.group-chart.participant-selection.placeholder')"
                    class="w-full"
                />
                <Message
                    v-if="selectionProblem"
                    :id="`group-chart-create_selection-problem-${project.id}`"
                    severity="warn"
                    size="small"
                    :closable="false"
                >
                    {{ t(`project-details.group-chart.create.${selectionProblem}`, { role: feedbackRecipientRole?.name }) }}
                </Message>
            </div>
            <div v-if="showWithNameToggle" class="flex flex-col gap-1">
                <div class="flex items-center gap-2">
                    <Checkbox
                        :input-id="`group-chart-create_with-name-${project.id}`"
                        v-model="withName"
                        binary
                    />
                    <label :for="`group-chart-create_with-name-${project.id}`" class="text-sm font-bold">
                        {{ t("project-details.group-chart.create.with-name-field") }}
                    </label>
                </div>
                <small class="text-content-muted">
                    {{ t("project-details.group-chart.create.with-name-hint") }}
                </small>
            </div>
        </div>
        <div class="flex justify-end gap-2 mt-2">
            <Button type="button" :label="t('buttons.cancel')" severity="secondary" @click="closeDialog" />
            <Button
                :id="`group-chart-create_generate-${project.id}`"
                type="button"
                :label="t('project-details.group-chart.generate')"
                icon="pi pi-check"
                :disabled="selectedParticipants.length === 0 || !!selectionProblem || loading"
                :loading="loading"
                @click="generate"
            />
        </div>
    </Dialog>
</template>
